'use client';

import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';
import { statics } from '@/data/store';
import FilterTable from './FilterTable';
import { COLORS } from '@/constants/colors';
import LoadingOverlay from '@/components/LoadingOverlay';
import FAQSection from '@/components/FAQSection';


const harleyFaqs = [
  {
    id: 1,
    question: 'How do I find the right Harley air filter?',
    answer:
      'Use the filter table to compare inlet diameter, height, base outside diameter, top outside diameter, style, end cap, inlet type, and color. Matching these measurements helps you choose the correct Harley air filter for your setup.',
  },
  {
    id: 2,
    question: 'What measurements do I need for a motorcycle cone air filter?',
    answer:
      'For a motorcycle cone air filter, check the inlet diameter, filter height, outside diameter at the base, and outside diameter at the top. These measurements help confirm proper fitment before ordering.',
  },
  {
    id: 3,
    question: 'How do I clean a Harley air filter?',
    answer:
      'Remove the Harley air filter, apply the proper filter cleaner, rinse gently, let it dry completely, then re-oil it before reinstalling. Always follow the filter care instructions and avoid reinstalling the filter while it is still wet.',
  },
  {
    id: 4,
    question: 'How often should I clean a Harley Davidson air filter?',
    answer:
      'Cleaning frequency depends on riding conditions. For normal use, inspect the filter regularly and clean it when dirt buildup is visible. Dusty roads, long rides, or performance use may require more frequent cleaning.',
  },
  {
    id: 5,
    question: 'Are Green Filter Harley air filters washable and reusable?',
    answer:
      'Yes. Green Filter Harley air filters are washable and reusable. They can be cleaned, re-oiled, and reinstalled for long-term use, helping reduce replacement waste while supporting airflow and filtration.',
  },
];

const tableHeader = [
  {
    id: 0,
    name: 'title',
    label: 'Filter Number',
    maxWidth: 91,
  },
  {
    id: 1,
    name: 'filter_id_length_inch',
    label: 'Inlet Diameter (ID)',
    maxWidth: 165,
  },
  {
    id: 2,
    name: 'filter_height_inch',
    label: 'Height (H)',
    maxWidth: 82,
  },
  {
    id: 3,
    name: 'filter_od_base_length_inch',
    label: 'Outside Diameter - Base (OD-B)',
    maxWidth: 171,
  },
  {
    id: 4,
    name: 'filter_od_top_length_inch',
    label: 'Outside Diameter - Top (OD-T)',
    maxWidth: 171,
  },
  {
    id: 5,
    name: 'filter_style',
    label: 'Style',
    maxWidth: 165,
  },
  {
    id: 6,
    name: 'filter_end_cap',
    label: 'End Cap',
    maxWidth: 179,
  },
  {
    id: 7,
    name: 'filter_inlet_type',
    label: 'Inlet Type',
    maxWidth: 131,
  },
  {
    id: 8,
    name: 'filter_color',
    label: 'Color',
    maxWidth: 76,
  },
];

const HFElement1 = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getTableData();
  }, []);

  const getTableData = async () => {
    setLoading(true);
    const product_type_id = 10;

    const url = `${statics.BaseUrl}/product?product_type_id=${product_type_id}&pageNo=1&pageSize=200&is_active=1`;

    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.ResponseCode === 'Success') {
        const rows = Array.isArray(data?.data) ? data.data : data?.data?.rows || [];
        setTableData(rows);
      }
    } catch (error) {
      console.error('Failed to fetch Harley filters:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay loading={loading} />

      <HFElement1Wrapper>
        <PageTitle>Harley Air Filter</PageTitle>

        <IntroText>
          Find the right Green Filter for your Harley-Davidson motorcycle. Our washable, reusable motorcycle air
          filters are built for high-flow performance, reliable filtration, and long-term value. Use the filter
          table below to compare inlet diameter, height, outside diameter, style, end cap, inlet type, and color.
        </IntroText>

        <SectionTitle>Harley-Davidson Air Filter Size Guide</SectionTitle>
        <GuideText>
          Use the measurements below to choose the correct Harley air filter for your setup. All measurements are
          listed in inches. If you are replacing a clamp-on or motorcycle cone air filter, confirm the inlet
          diameter, filter height, base diameter, and top diameter before ordering.
        </GuideText>

        <SectionTitle>Cone Filter Measurements</SectionTitle>
        <MeasurementLinkText>
          <a
            href="/assets/cylinders/cone_filter_measurements_s.webp"
            style={{ color: COLORS.brandGreen, fontWeight: 600 }}
            title="Green Filter USA - Cone & Cylinder Filter Dimensions"
          >
            Click here
          </a>{' '}
          for a picture showing how cone and cylinder filters are measured.
        </MeasurementLinkText>

        <ImageGrid>
          <ImageBox>
            <Image className="resize" alt="Harley Davidson Air Filter" src="/assets/harley.webp" width={200} height={124} />
          </ImageBox>
          <ImageBox>
            <a
              href="https://www.youtube.com/watch?v=Gz90LhIE08E&ab_channel=GreenFilter"
              className="videomediumpic"
              style={{ color: COLORS.brandGreen }}
            >
              <Image
                src="/assets/cylinders/vid_measure_350.webp"
                className="resize"
                alt="Video: Measuring and Finding a Clamp-On Filter"
                width={350}
                height={200}
              />
              <br />
              Green Filter USA - Measuring and Finding a Clamp-On Filter
            </a>
          </ImageBox>
          <ImageBox>
            <a href="/assets/cylinders/cone_filter_measurements_s.webp" style={{ color: COLORS.brandGreen }}>
              <Image
                src="/assets/cylinders/cone_filter_measurements_s.webp"
                className="resize"
                alt="Cone Filter Measurements"
                width={150}
                height={174}
              />
            </a>
          </ImageBox>
        </ImageGrid>

        <BenefitsSection>
          <BenefitsTitle>Why Choose Green Filter for Harley Davidson Motorcycles</BenefitsTitle>
          <BenefitsList>
            <BenefitItem>High-flow motorcycle air filter design</BenefitItem>
            <BenefitItem>Washable and reusable for long-term use</BenefitItem>
            <BenefitItem>Made in the USA</BenefitItem>
            <BenefitItem>Built for reliable fitment and performance</BenefitItem>
            <BenefitItem>Lifetime warranty support</BenefitItem>
          </BenefitsList>
        </BenefitsSection>

        <h4 style={{ padding: '0 10px' }}>
          Select a Harley filter from the list below. The list can be sorted by clicking on a column heading. Clicking the heading again will sort it in the opposite direction. The table will scroll left and right for smaller screens.
        </h4>
        <p style={{ textAlign: 'center' }}>
          <strong>All measurements are in inches.</strong>
        </p>

        <FilterTable headers={tableHeader} tableData={tableData} />

        <ClosingSection>
          <ClosingText>
            Need help finding the right Harley air filter? Use our{' '}
            <a href="/shop-by-vehicle">vehicle selector</a> or{' '}
            <a href="/contact-us">contact us</a> for expert guidance on Harley-Davidson air filters.
          </ClosingText>
        </ClosingSection>
      </HFElement1Wrapper>

      <FAQSection faqs={harleyFaqs} />
    </>
  );
};

export default React.memo(HFElement1);

const IntroText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-family: Lato, sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 40px 0 16px;
  padding: 0 20px;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 20px;
    margin: 30px 0 12px;
  }
`;

const GuideText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 14px;
    margin-bottom: 20px;
  }
`;

const MeasurementLinkText = styled.p`
  max-width: 800px;
  margin: 0 auto 30px;
  padding: 0 20px;
  font-family: Lato, sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: #555;
  line-height: 1.7;
  text-align: center;

  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const ImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin: 30px 0;
  padding: 0 20px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ImageBox = styled.div`
  flex: 1;
  min-width: 280px;
  max-width: 350px;
  text-align: center;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }

  .videomediumpic {
    font-family: Lato, sans-serif;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    display: block;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const BenefitsSection = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px 40px;
  background: #f9f9f9;
  border-radius: 12px;

  @media (max-width: 767px) {
    margin: 30px 20px;
    padding: 24px 20px;
  }
`;

const BenefitsTitle = styled.h3`
  font-family: Lato, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px;
  text-align: center;

  @media (max-width: 767px) {
    font-size: 18px;
    margin-bottom: 16px;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const BenefitItem = styled.li`
  font-family: Lato, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  background: #fff;
  padding: 10px 16px;
  border-radius: 20px;
  border: 1px solid #e0e0e0;

  @media (max-width: 767px) {
    font-size: 13px;
    text-align: center;
  }
`;

const ClosingSection = styled.div`
  max-width: 800px;
  margin: 30px auto 50px;
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  text-align: center;

  @media (max-width: 767px) {
    margin: 20px auto 30px;
    padding: 15px;
  }
`;

const ClosingText = styled.p`
  font-family: Lato, sans-serif;
  font-size: 15px;
  color: #555;
  margin: 0;
  line-height: 1.6;

  a {
    color: ${COLORS.brandGreen};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 767px) {
    font-size: 14px;
  }
`;

const PageTitle = styled.h1`
  font-family: Lato, sans-serif;
  font-size: 38px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 20px;

  @media (max-width: 767px) {
    font-size: 26px;
    margin: 0 0 16px;
  }
`;

const HFElement1Wrapper = styled.div`
  padding: 20px;
  background: #fff;

  h2 {
    font-family: Lato, sans-serif;
    font-size: 28px;
    font-weight: 700;
    color: #1a1a1a;
    margin-bottom: 30px;
  }

  h4 {
    font-family: Lato, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #333;
    line-height: 1.6;
    margin-bottom: 20px;
  }

  p {
    font-family: Lato, sans-serif;
    font-size: 15px;
    color: #333;
    margin-bottom: 30px;
  }

  .resize {
    max-width: 100%;
    height: auto;
  }

  @media (max-width: 767px) {
    padding: 15px;

    h2 {
      font-size: 22px;
    }

    h4 {
      font-size: 14px;
    }
  }
`;
